import { HistoryBucket, HistoryLevel, ServiceHistory, ServiceStatusDoc, ServiceStatusEntry } from '@/types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Width of one bar.
 *
 * Fifteen minutes, not one hour, purely so the strip LOOKS like a status-page
 * strip: 96 thin bars across the panel reads as a timeline the way 24 fat
 * blocks read as a heatmap. The resolution is honest — the aggregate stores
 * exact per-occurrence timestamps, so nothing is interpolated to fill them.
 */
export const BUCKET_MS = 15 * MINUTE_MS;

/**
 * How far back the strip can honestly go.
 *
 * The backend (`app/core/service_status.py`) prunes aggregate entries at 24h,
 * so 24 hourly buckets is the entire history that exists. Every 90-day strip on
 * every status page we looked at is backed by an uptime probe we do not run;
 * drawing 90 bars here would mean inventing 89 days of green. See the note
 * rendered under the strip.
 */
export const HISTORY_HOURS = 24;

/** Number of bars in a full strip. */
export const HISTORY_BUCKETS = (HISTORY_HOURS * HOUR_MS) / BUCKET_MS;

/**
 * Mirrors `AGGREGATE_CAP` in `app/core/service_status.py`.
 *
 * A service that hits the cap has had its oldest entries dropped, so the early
 * hours of its strip are unknown rather than quiet — and must not be drawn as
 * if we checked them.
 */
const AGGREGATE_CAP = 200;

function entryTime(entry: ServiceStatusEntry | undefined): number | null {
  const at = entry?.ts?.toDate?.().getTime();
  return typeof at === 'number' && Number.isFinite(at) ? at : null;
}

function levelFor(count: number, criticalCount: number): HistoryLevel {
  if (criticalCount > 0) return 'critical';
  if (count > 0) return 'errors';
  return 'quiet';
}

/**
 * Hour-by-hour fault history for one service, derived from the same public
 * aggregate the health verdict uses. No new data source, nothing synthesised.
 *
 * `now` is injectable so bucket boundaries are testable and so a whole render
 * shares one clock instead of drifting between services.
 */
export function buildServiceHistory(
  doc: ServiceStatusDoc | undefined,
  now: number = Date.now(),
  hours: number = HISTORY_HOURS
): ServiceHistory {
  const bucketCount = (hours * HOUR_MS) / BUCKET_MS;
  const entries = doc?.recent ?? [];
  const times = entries
    .map(entryTime)
    .filter((t): t is number => t !== null);

  // Buckets are anchored to a bucket boundary rather than to `now`, so the
  // rightmost bar always means "this interval" and bars don't slide under the
  // reader while they watch.
  const currentStart = Math.floor(now / BUCKET_MS) * BUCKET_MS;
  const oldestStart = currentStart - (bucketCount - 1) * BUCKET_MS;

  // At the cap the aggregate is a truncated tail: hours before its oldest
  // surviving entry were overwritten, and we cannot claim they were quiet.
  const truncated = entries.length >= AGGREGATE_CAP;
  const oldestKnown = times.length > 0 ? Math.min(...times) : null;
  const coverageStart = truncated && oldestKnown !== null ? oldestKnown : oldestStart;

  const counts = new Map<number, { count: number; criticalCount: number }>();
  for (const entry of entries) {
    const at = entryTime(entry);
    if (at === null || at < oldestStart) continue;
    const bucketStart = Math.floor(at / BUCKET_MS) * BUCKET_MS;
    const bucket = counts.get(bucketStart) ?? { count: 0, criticalCount: 0 };
    counts.set(bucketStart, {
      count: bucket.count + 1,
      criticalCount: bucket.criticalCount + (entry.sev === 'critical' ? 1 : 0),
    });
  }

  const buckets: HistoryBucket[] = Array.from({ length: bucketCount }, (_, i) => {
    const start = oldestStart + i * BUCKET_MS;
    const tally = counts.get(start) ?? { count: 0, criticalCount: 0 };
    // An interval we have no records for is "uncovered", not "quiet". The strip
    // must never imply we looked at a period whose data was discarded.
    const level: HistoryLevel =
      start + BUCKET_MS <= coverageStart && tally.count === 0
        ? 'uncovered'
        : levelFor(tally.count, tally.criticalCount);

    return { start: new Date(start), level, ...tally };
  });

  return {
    buckets,
    truncated,
    coverageFrom: new Date(coverageStart),
  };
}
